import { environment } from '../../../../environments/environment';
import type { DealerDevice } from '../../models/entities/dealer-api.model';
import type {
  EventCameraItem,
  EventCameraTimeZone,
  EventCamerasResponse,
  EventTalkdownGroup,
  EventTalkdownMessageOption,
  EventTalkdownsResponse,
} from '../../models/entities/event-detail.model';

/** Dealer site device type for NVRs. */
export const DEALER_DEVICE_TYPE_NVR = 1;

/** Dealer site device type for cameras. */
export const DEALER_DEVICE_TYPE_CAMERA = 2;

/** Dealer site device type for talkdown speakers. */
export const DEALER_DEVICE_TYPE_SPEAKER = 9;

const DEFAULT_MEDIA_MTX_PORT = 8186;

function isNvr(device: DealerDevice): boolean {
  return Number(device.deviceTypeId) === DEALER_DEVICE_TYPE_NVR;
}

function isCamera(device: DealerDevice): boolean {
  return Number(device.deviceTypeId) === DEALER_DEVICE_TYPE_CAMERA;
}

function isSpeaker(device: DealerDevice): boolean {
  return Number(device.deviceTypeId) === DEALER_DEVICE_TYPE_SPEAKER;
}

function normalizeHost(value: string | null | undefined): string {
  return (
    value
      ?.trim()
      .replace(/^rtsp:\/\//i, '')
      .replace(/^[^@/]+@/, '')
      .replace(/\/+$/, '')
      .replace(/:\d+$/, '') ?? ''
  );
}

function normalizeStreamPath(value: string | null | undefined): string {
  return value?.trim().replace(/^\/+/, '') ?? '';
}

function resolveNvrMediaMtxPort(nvr: DealerDevice): number {
  const configured = Number(nvr.details?.mediaMTXPort);
  if (Number.isInteger(configured) && configured > 0 && configured <= 65_535) {
    return configured;
  }
  return DEFAULT_MEDIA_MTX_PORT;
}

function findParentNvr(
  camera: DealerDevice,
  devices: readonly DealerDevice[],
): DealerDevice | null {
  const parentId = Number(camera.parentId);
  if (!Number.isInteger(parentId) || parentId <= 0) {
    return null;
  }
  return devices.find((device) => isNvr(device) && Number(device.id) === parentId) ?? null;
}

function asNonBlankCredential(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

function resolveRtspCredential(
  details: DealerDevice['details'],
  key: 'rtspUsername' | 'rtspPassword',
  fallback: string,
): string {
  const fromDetails = asNonBlankCredential(details?.[key]);
  return fromDetails != null ? encodeURIComponent(fromDetails) : fallback;
}

function buildRtspUrl(
  host: string,
  port: number,
  streamPath: string | null | undefined,
  details: DealerDevice['details'],
): string {
  const path = normalizeStreamPath(streamPath);

  if (!host || !path) {
    return '';
  }

  const username = resolveRtspCredential(
    details,
    'rtspUsername',
    environment.streaming.rtspUsername,
  );
  const password = resolveRtspCredential(
    details,
    'rtspPassword',
    environment.streaming.rtspPassword,
  );
  return `rtsp://${username}:${password}@${host}:${port}/${path}`;
}

/** Dealer camera details may use camelCase or PascalCase. */
function resolveCameraIsPtz(device: DealerDevice): boolean {
  const details = device.details;
  if (!details || typeof details !== 'object') {
    return false;
  }
  const record = details as {
    isPtz?: boolean | null;
    isPTZ?: boolean | null;
    IsPtz?: boolean | null;
    IsPTZ?: boolean | null;
  };
  return (
    record.isPtz === true ||
    record.isPTZ === true ||
    record.IsPtz === true ||
    record.IsPTZ === true
  );
}

const FALLBACK_CAMERA_TIME_ZONE: EventCameraTimeZone = {
  olson: 'UTC',
  isDstIgnored: false,
};

function mapParentNvrTimeZone(nvr: DealerDevice | null): EventCameraTimeZone {
  const raw = nvr?.details?.timeZone;
  if (!raw || typeof raw !== 'object') {
    return FALLBACK_CAMERA_TIME_ZONE;
  }

  const olson = typeof raw.olson === 'string' ? raw.olson.trim() : '';
  return {
    olson: olson || FALLBACK_CAMERA_TIME_ZONE.olson,
    ...(typeof raw.utc === 'string' ? { utc: raw.utc } : {}),
    ...(typeof raw.dst === 'string' ? { dst: raw.dst } : {}),
    isDstIgnored: Boolean(raw.isDstIgnored),
  };
}

function asNonBlankDetail(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
}

function mapCameraDisarmMetadata(details: DealerDevice['details']): Pick<
  EventCameraItem,
  'disarmingNotes' | 'disarmedAt' | 'disarmedEndAt' | 'disarmedBy'
> {
  if (details?.isArmed === true) {
    return { disarmingNotes: null, disarmedAt: null, disarmedEndAt: null, disarmedBy: null };
  }

  return {
    disarmingNotes: asNonBlankDetail(details?.disarmedReason),
    disarmedAt: asNonBlankDetail(details?.disarmedDateTime),
    disarmedEndAt: asNonBlankDetail(details?.disarmedEndDateTime),
    disarmedBy: asNonBlankDetail(details?.disarmedByUserName),
  };
}

function mapCameraDevice(device: DealerDevice, devices: readonly DealerDevice[]): EventCameraItem {
  const numericId = Number(device.id);
  const nvr = findParentNvr(device, devices);
  const host = nvr ? normalizeHost(nvr.details?.ipAddress) : '';
  const port = nvr ? resolveNvrMediaMtxPort(nvr) : DEFAULT_MEDIA_MTX_PORT;
  const rtspStream1 = device.details?.rtspStream1 ?? null;
  const rtspStream2 = device.details?.rtspStream2 ?? null;
  return {
    id: String(numericId),
    numericId,
    siteId: Number(device.siteId),
    name: device.name?.trim() ?? `Camera ${numericId}`,
    enabledAiEvents: [],
    isPtz: resolveCameraIsPtz(device),
    status: device.details?.isArmed === true ? 'armed' : 'disarmed',
    ...mapCameraDisarmMetadata(device.details),
    timeZone: mapParentNvrTimeZone(nvr),
    nvrId: nvr ? Number(nvr.id) : null,
    nvrName: nvr?.name?.trim() || null,
    streamUrls: {
      main: buildRtspUrl(host, port, rtspStream1, nvr?.details),
      sub: buildRtspUrl(host, port, rtspStream2, nvr?.details),
    },
  };
}

function talkdownMessageLabelLines(name: string | null | undefined): string[] {
  const trimmed = name?.trim();
  if (!trimmed) {
    return ['Message'];
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return [parts[0], parts.slice(1).join(' ')];
  }

  return [trimmed];
}

function mapSpeakerMessageOptions(speaker: DealerDevice): EventTalkdownMessageOption[] {
  const messages = speaker.details?.messages ?? [];
  return messages
    .filter((message) => Number.isInteger(Number(message.id)) && Number(message.id) > 0)
    .map((message) => ({
      id: String(message.id),
      messageId: Number(message.id),
      labelLines: talkdownMessageLabelLines(message.name),
    }));
}

/** Dealer camera details may use camelCase or PascalCase. */
function readNearestTalkDownId(details: DealerDevice['details']): number {
  if (!details || typeof details !== 'object') {
    return 0;
  }
  const record = details as {
    nearestTalkDown?: number | string | null;
    NearestTalkDown?: number | string | null;
  };
  const raw = record.nearestTalkDown ?? record.NearestTalkDown;
  const talkdownId = Number(raw);
  return Number.isInteger(talkdownId) && talkdownId > 0 ? talkdownId : 0;
}

function cameraLabel(camera: DealerDevice): string {
  const numericId = Number(camera.id);
  return camera.name?.trim() || `Camera ${numericId}`;
}

/** Maps Dealer site devices into Event View camera rows (deviceTypeId = 2). */
export function mapDealerDevicesToCameras(
  devices: DealerDevice[] | null | undefined,
): EventCamerasResponse {
  const allDevices = devices ?? [];
  const items = allDevices.filter(isCamera).map((camera) => mapCameraDevice(camera, allDevices));
  return { items };
}

/** Maps Dealer speaker devices into Event View talkdown groups (deviceTypeId = 9). */
export function mapDealerDevicesToTalkdowns(
  devices: DealerDevice[] | null | undefined,
): EventTalkdownsResponse {
  const allDevices = devices ?? [];
  const speakers = allDevices.filter(isSpeaker);
  const speakerIds = new Set(speakers.map((speaker) => Number(speaker.id)));
  const camerasByTalkdown = new Map<number, { id: string; label: string }[]>();

  for (const camera of allDevices) {
    if (!isCamera(camera)) {
      continue;
    }
    const talkdownId = readNearestTalkDownId(camera.details);
    if (!speakerIds.has(talkdownId)) {
      continue;
    }
    const linked = camerasByTalkdown.get(talkdownId) ?? [];
    linked.push({ id: String(Number(camera.id)), label: cameraLabel(camera) });
    camerasByTalkdown.set(talkdownId, linked);
  }

  const items: EventTalkdownGroup[] = speakers.map((speaker) => {
    const numericId = Number(speaker.id);
    const linked = camerasByTalkdown.get(numericId) ?? [];
    const messageOptions = mapSpeakerMessageOptions(speaker);

    return {
      id: String(numericId),
      speakerName: speaker.name?.trim() ?? `Speaker ${speaker.id}`,
      cameraIds: linked.map((camera) => camera.id),
      cameraLabels: linked.map((camera) => camera.label),
      ...(messageOptions.length ? { messageOptions } : {}),
    };
  });

  return { items };
}
